import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Copy, Check } from 'lucide-react';
import { api } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import { estadoInvitado, loQueQueda, AVISO_UNA_VEZ } from '../ui/invitados';

// ACCESOS DE AUDITOR · Fase 2 del despegue.
//
// La pantalla existe para dos momentos muy distintos: crear un acceso
// con alguien esperando delante, y —meses después— contestar «¿qué vio
// aquel auditor?». Lo primero pide que sea corto; lo segundo, que no se
// borre nada.
export default function Invitados() {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [visitas, setVisitas] = useState(null);

  const cargar = useCallback(
    () => api.get('/invitados').then(setFilas).catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setError('');
    try {
      const d = await api.post('/invitados', {
        auditor: f.get('auditor'),
        cliente: f.get('cliente') || null,
        motivo: f.get('motivo') || null,
        horas: Number(f.get('horas')) || 24,
      });
      setNuevo(d);
      setCreando(false);
      setCopiado(false);
      cargar();
    } catch (err) { setError(err.message); }
  };

  const retirar = async (id) => {
    try { await api.post(`/invitados/${id}/revocar`, {}); cargar(); }
    catch (err) { setError(err.message); }
  };

  const enlaceDe = (credencial) =>
    `${window.location.origin}/auditor#${credencial}`;

  return (
    <>
      <ListaEstandar
        titulo="Accesos de auditor"
        subtitulo="Enlaces de solo lectura para que un auditor vea tu documentación de calidad sin usar la cuenta de nadie."
        nota={
          <NotaExplicativa titulo="Qué puede y qué no puede hacer un auditor con esto">
            Caduca solo el día que tú digas, lo puedes
            retirar en cualquier momento, y{' '}
            <strong>queda apuntado todo lo que miró</strong> — eso no se
            borra ni al retirar el acceso.
          </NotaExplicativa>
        }
        cargando={filas === null}
        error={error}
        accionPrincipal={
          <button type="button" onClick={() => setCreando(true)}
                  className="inline-flex items-center gap-2 rounded-lg
                             bg-[var(--color-marca)] px-3 py-2 text-sm
                             font-semibold text-white">
            <KeyRound className="h-4 w-4" aria-hidden="true" /> Dar acceso a un auditor
          </button>
        }
        vacio={
          <EstadoVacio
            icono={KeyRound}
            titulo="Todavía no le has dado acceso a ningún auditor"
            descripcion="Cuando llegue uno, dale un enlace propio en vez de prestarle tu sesión: verá solo lo de calidad, no podrá tocar nada, y sabrás exactamente qué miró."
          />
        }
        columnas={[
          { etiqueta: 'Auditor', valor: (f) => f.auditor },
          { etiqueta: 'De parte de', valor: (f) => f.cliente || '—' },
          {
            etiqueta: 'Estado',
            valor: (f) => <ChipEstado {...estadoInvitado(f.estado)} />,
          },
          { etiqueta: 'Vigencia', valor: (f) => loQueQueda(f.caduca_en) },
          { etiqueta: 'Lo abrió', valor: (f) => (f.usos ? `${f.usos} vez(ces)` : 'Todavía no') },
          { etiqueta: 'Miró', valor: (f) => `${f.visitas} cosa(s)` },
        ]}
        filas={filas || []}
        acciones={(f) => [
          { etiqueta: 'Ver qué miró',
            onClick: () => api.get(`/invitados/${f.id}/visitas`)
              .then((v) => setVisitas({ fila: f, lista: v })) },
          ...(f.estado === 'ACTIVO'
            ? [{ etiqueta: 'Retirar', onClick: () => retirar(f.id) }]
            : []),
        ]}
      />

      {creando && (
        <Modal titulo="Dar acceso a un auditor" onCerrar={() => setCreando(false)}>
          <form onSubmit={crear} className="flex flex-col gap-3">
            <Ayuda id="invitados.que_ve" />
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Nombre del auditor</span>
              <input name="auditor" required minLength={3}
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <span className="text-gray-500">
                Con nombre y apellido. Dentro de seis meses, «auditor» no
                le dice nada a nadie.
              </span>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">A quién representa</span>
              <input name="cliente"
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <span className="text-gray-500">
                El cliente que audita. Es lo que se busca después.
              </span>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Cuánto dura</span>
              <select name="horas" defaultValue="24"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                <option value="8">Lo que dura el día (8 horas)</option>
                <option value="24">Un día</option>
                <option value="72">Tres días</option>
                <option value="168">Una semana</option>
              </select>
              <span className="text-gray-500">
                Cuando se cumpla el plazo, el enlace deja de abrir solo.
                No hace falta que te acuerdes de retirarlo.
              </span>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Motivo (opcional)</span>
              <input name="motivo"
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>
            <button type="submit"
                    className="mt-1 rounded-lg bg-[var(--color-marca)] px-3 py-2
                               text-sm font-semibold text-white">
              Crear el acceso
            </button>
          </form>
        </Modal>
      )}

      {nuevo && (
        <Modal titulo="El enlace del auditor" onCerrar={() => setNuevo(null)}>
          <p className="text-sm text-gray-700">
            Dáselo a <strong>{nuevo.auditor}</strong>. Vale hasta el{' '}
            {new Date(nuevo.caduca_en).toLocaleString('es-MX')}.
          </p>
          <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3
                        text-sm font-semibold text-amber-900">
            {AVISO_UNA_VEZ}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 overflow-auto rounded-lg bg-gray-100 p-2
                             text-xs text-gray-800">
              {enlaceDe(nuevo.credencial)}
            </code>
            <button type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(enlaceDe(nuevo.credencial));
                      setCopiado(true);
                    }}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg
                               border border-gray-300 px-3 py-2 text-sm font-semibold">
              {copiado
                ? <><Check className="h-4 w-4 text-green-700" aria-hidden="true" /> Copiado</>
                : <><Copy className="h-4 w-4" aria-hidden="true" /> Copiar</>}
            </button>
          </div>
        </Modal>
      )}

      {visitas && (
        <Modal titulo={`Qué miró ${visitas.fila.auditor}`}
               onCerrar={() => setVisitas(null)}>
          {visitas.lista.length === 0 ? (
            <p className="text-sm text-gray-600">
              Sin visitas al enlace.
            </p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {visitas.lista.map((v, i) => (
                <li key={i} className="flex justify-between gap-3
                                       border-b border-gray-100 pb-1">
                  <span className="text-gray-800">
                    {v.que}
                    {v.detalle && (
                      <span className="text-gray-500"> · {v.detalle}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-gray-500">
                    {new Date(v.cuando).toLocaleString('es-MX')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Esta lista no se borra, ni siquiera al retirar el acceso: es la
            constancia de qué se le enseñó a quién.
          </p>
        </Modal>
      )}
    </>
  );
}
